import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js/pure'
import { useTheme } from '@mui/material/styles'
import {
	createStripeCapitalAccountSession,
	getCapitalOfferStatus
} from 'apis/consolidated-practice'
import { buildCapitalStripeAppearance } from 'theme/capital'
import type { CapitalOfferSummary } from 'util/capital'
import { STRIPE } from '../config'

export interface UseStripeCapitalConnectOptions {
	/**
	 * Offer-query refetch interval (ms). `0`/undefined disables polling.
	 * Caller owns phase changes and bounding the total window.
	 */
	pollIntervalMs?: number
	/**
	 * When true, fetch a fresh session and build a `StripeConnectInstance`.
	 * The page passes `true`; sidebar consumers that only read offer data
	 * leave it `false` (default) so they never claim a client secret.
	 */
	buildConnectInstance?: boolean
}

export interface UseStripeCapitalConnectResult {
	stripeConnectInstance: StripeConnectInstance | undefined
	error: Error | undefined
	offerId: string | null
	offerAmount: number | null
	offerStatus: string | null
	/** Backend's verdict on whether to render the promo page over Stripe's financing component. */
	isPromoOffer: boolean
	/** True once the offer query settled — `null` offer fields are real, not loading. */
	offerResolved: boolean
	/**
	 * Tear down the current instance and refetch everything. Use after actions
	 * that mutate the server-side offer state (e.g. application submission).
	 */
	refresh: () => void
}

const useStripeCapitalConnect = (
	consolidatedPracticeId: string | undefined,
	options: UseStripeCapitalConnectOptions = {}
): UseStripeCapitalConnectResult => {
	const { pollIntervalMs = 0, buildConnectInstance = false } = options
	const theme = useTheme()
	const queryClient = useQueryClient()
	const appearanceVariables = useMemo(() => buildCapitalStripeAppearance(theme), [theme])

	// Bumped by `refresh()` to re-run the Connect instance build effect.
	const [refreshKey, setRefreshKey] = useState(0)

	const offerQueryKey = useMemo(
		() => ['capital-offer-status', consolidatedPracticeId] as const,
		[consolidatedPracticeId]
	)

	// Shared offer-status query. Same key across all consumers (page + sidebar
	// tile) so the page's polling updates the tile's view without each consumer
	// hitting the endpoint separately.
	const { data, error: queryError } = useQuery<CapitalOfferSummary>({
		queryKey: offerQueryKey,
		queryFn: async () => {
			const result = await getCapitalOfferStatus(consolidatedPracticeId as string)
			return result.data
		},
		enabled: Boolean(consolidatedPracticeId),
		refetchOnWindowFocus: false,
		refetchInterval: pollIntervalMs > 0 ? pollIntervalMs : false,
		retry: 1
	})

	const offerId = data?.offerId ?? null
	const offerAmount = data?.offerAmount ?? null
	const offerStatus = data?.offerStatus ?? null
	const isPromoOffer = data?.promo === true

	const instanceRef = useRef<StripeConnectInstance | null>(null)
	// Tracks the practice id the existing instance was built for, so we can
	// tear it down + rebuild when the user switches practices.
	const instancePracticeIdRef = useRef<string | undefined>()
	const [stripeConnectInstance, setStripeConnectInstance] = useState<
		StripeConnectInstance | undefined
	>()

	const teardownInstance = (): void => {
		if (!instanceRef.current) return
		try {
			instanceRef.current.logout()
		} catch (err) {
			console.error('useStripeCapitalConnect: logout failed', err)
		}
		instanceRef.current = null
	}

	// Practice-switch eviction. The offer query's `data` becomes `undefined` as
	// soon as `consolidatedPracticeId` changes (no `keepPreviousData`), but the
	// instance-build effect below early-returns on `!data`, so without this
	// pre-effect the previous practice's instance would stay mounted on a
	// stale session until the new query resolves — which surfaces as the embed
	// "loading then disappearing" symptom on practice switch. Tear down + drop
	// the rendered instance synchronously here so the page falls back to its
	// loading-card branch immediately, then the build effect rebuilds once the
	// new offer data is available.
	useEffect(() => {
		if (!instancePracticeIdRef.current) return
		if (instancePracticeIdRef.current === consolidatedPracticeId) return
		// Skip transient `undefined` ids (e.g. parent unmounting `PracticeContext`
		// momentarily) — tearing down on those would force an unnecessary embed
		// rebuild when the same id resolves on the next render.
		if (!consolidatedPracticeId) return
		teardownInstance()
		instancePracticeIdRef.current = undefined
		setStripeConnectInstance(undefined)
	}, [consolidatedPracticeId])

	useEffect(() => {
		if (!buildConnectInstance || !consolidatedPracticeId || !data) return

		if (instanceRef.current) {
			setStripeConnectInstance(instanceRef.current)
			return
		}

		// Snapshot the practice id by value so a session refetch on this
		// instance can never be hijacked by a later practice switch — the
		// closure must mint secrets for the account this instance was built
		// against, even if the hook has since rerendered with a new id.
		// `appearanceVariables` is intentionally NOT snapshot — theme changes
		// flow to the live instance via the `update()` effect below so a switch
		// from light → dark mode reflects without a teardown.
		const instancePracticeId = consolidatedPracticeId
		const instance = loadConnectAndInitialize({
			publishableKey: STRIPE.publishableKey,
			// Stripe requires a freshly-minted secret on every call.
			fetchClientSecret: async () => {
				const fresh = await createStripeCapitalAccountSession(instancePracticeId)
				return fresh.data.clientSecret
			},
			appearance: {
				overlays: 'dialog',
				variables: appearanceVariables
			}
		})
		instanceRef.current = instance
		instancePracticeIdRef.current = consolidatedPracticeId
		setStripeConnectInstance(instance)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buildConnectInstance, consolidatedPracticeId, data, refreshKey])

	// Push theme changes to the existing instance.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance) return
		try {
			;(
				instance as unknown as {
					update: (opts: { appearance: { variables: Record<string, string> } }) => void
				}
			).update({ appearance: { variables: appearanceVariables } })
		} catch (err) {
			console.error('useStripeCapitalConnect: failed to update appearance', err)
		}
	}, [appearanceVariables])

	const refresh = (): void => {
		teardownInstance()
		instancePracticeIdRef.current = undefined
		setStripeConnectInstance(undefined)
		setRefreshKey((n) => n + 1)
		queryClient.invalidateQueries({ queryKey: offerQueryKey })
	}

	return {
		stripeConnectInstance,
		error: queryError ?? undefined,
		offerId,
		offerAmount,
		offerStatus,
		isPromoOffer,
		offerResolved: data !== undefined || queryError !== null,
		refresh
	}
}

export default useStripeCapitalConnect
